(function () {
  "use strict";

  const CONFIG = {
    quality: 80,
    lazyLoadMargin: "300px",
    breakpoints: [200, 400, 600, 800, 1000, 1200, 1600],
    formats: ["webp"],
    skipSelectors: [
      "[data-no-optimize]",
      ".no-optimize",
      'img[src*="data:"]',      // Skip base64
      'img[src*="svg"]',        // Skip SVGs
      'img[src*="logo"]',       // Optional: skip logos
    ],
  };

  // ─── UTILITY ────────────────────────────────────────────

  function isShopifyImage(src) {
    return src && (
      src.includes("cdn.shopify.com") ||
      src.includes("cdn.shopifycdn.com") ||
      src.includes("/cdn/shop/")
    );
  }

  function buildShopifyUrl(src, width, format = "webp") {
    try {
      // Remove existing size params first
      let cleanSrc = src
        .replace(/[_.](\d+)x(\d+)?(\.\w+)?(\?|$)/, "$4")
        .replace(/\?(width|w)=\d+&?/, "?")
        .replace(/&?(width|w)=\d+/, "")
        .replace(/\?$/, "");

      const url = new URL(cleanSrc.startsWith("//") ? "https:" + cleanSrc : cleanSrc);
      url.searchParams.set("width", width);
      url.searchParams.set("format", format);
      return url.toString();
    } catch (e) {
      return src;
    }
  }

  function shouldSkip(img) {
    if (!img.src || img.src === window.location.href) return true;
    if (img.hasAttribute("data-optimized")) return true;
    return CONFIG.skipSelectors.some(selector => {
      try { return img.matches(selector); } catch { return false; }
    });
  }

  // ─── CORE OPTIMIZER ─────────────────────────────────────

  function optimizeImage(img) {
    if (shouldSkip(img)) return;

    const src = img.src || img.getAttribute("data-src") || "";
    if (!isShopifyImage(src)) return;

    // Build srcset
    const srcset = CONFIG.breakpoints
      .map(w => `${buildShopifyUrl(src, w)} ${w}w`)
      .join(", ");

    // Natural width for sizes
    const displayWidth = img.getAttribute("width") ||
      img.offsetWidth ||
      img.closest("[class*='product']")?.offsetWidth ||
      800;

    const sizes = `
      (max-width: 480px) 100vw,
      (max-width: 768px) 50vw,
      (max-width: 1024px) 33vw,
      ${displayWidth}px
    `.trim();

    // Apply optimizations
    if (!img.srcset) img.srcset = srcset;
    if (!img.sizes) img.sizes = sizes;
    img.decoding = "async";

    // Set WebP src
    img.src = buildShopifyUrl(src, Math.min(parseInt(displayWidth) || 800, 1200));

    // Mark as done
    img.setAttribute("data-optimized", "true");
  }

  // ─── LAZY LOADING ────────────────────────────────────────

  function setupLazyLoad() {
    // Convert all non-critical images to lazy
    document.querySelectorAll("img").forEach((img, index) => {
      // Keep first 3 images eager (above fold)
      if (index < 3) {
        img.loading = "eager";
        img.fetchPriority = "high";
      } else {
        if (!img.loading) img.loading = "lazy";
      }
    });

    // IntersectionObserver for data-src images
    if (!("IntersectionObserver" in window)) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;

        const img = entry.target;

        if (img.dataset.src) {
          img.src = img.dataset.src;
          img.removeAttribute("data-src");
        }
        if (img.dataset.srcset) {
          img.srcset = img.dataset.srcset;
          img.removeAttribute("data-srcset");
        }

        optimizeImage(img);
        observer.unobserve(img);
      });
    }, {
      rootMargin: CONFIG.lazyLoadMargin,
      threshold: 0.01
    });

    document.querySelectorAll("img[data-src]").forEach(img => observer.observe(img));
  }

  // ─── GIF → VIDEO CONVERTER ───────────────────────────────

  function convertGifsToVideo() {
    document.querySelectorAll('img[src$=".gif"], img[data-src$=".gif"]').forEach(img => {
      const src = img.src || img.dataset.src;
      if (!src) return;

      const video = document.createElement("video");
      video.autoplay = true;
      video.loop = true;
      video.muted = true;
      video.playsInline = true;
      video.className = img.className;
      video.style.cssText = img.style.cssText;
      video.setAttribute("aria-label", img.alt || "");

      // Copy dimensions
      if (img.width) video.width = img.width;
      if (img.height) video.height = img.height;

      video.innerHTML = `
        <source src="${src.replace(".gif", ".webm")}" type="video/webm">
        <source src="${src.replace(".gif", ".mp4")}" type="video/mp4">
      `;

      img.parentNode?.replaceChild(video, img);
    });
  }

  // ─── BACKGROUND IMAGES ───────────────────────────────────

  function optimizeBackgroundImages() {
    document.querySelectorAll("[style*='background-image']").forEach(el => {
      const style = el.getAttribute("style") || "";
      const match = style.match(/url\(['"]?(.*?)['"]?\)/);
      if (!match) return;

      const src = match[1];
      if (!isShopifyImage(src)) return;

      const width = el.offsetWidth || 800;
      const optimizedSrc = buildShopifyUrl(src, width);
      el.style.backgroundImage = `url('${optimizedSrc}')`;
    });
  }

  // ─── PRELOAD LCP IMAGE ───────────────────────────────────

  function preloadLCPImage() {
    const firstImg = document.querySelector(
      ".hero img, .banner img, [class*='hero'] img, [class*='banner'] img, img:first-of-type"
    );

    if (!firstImg) return;

    const src = firstImg.src;
    if (!isShopifyImage(src)) return;

    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = buildShopifyUrl(src, 1200);
    link.setAttribute("imagesrcset",
      CONFIG.breakpoints.map(w => `${buildShopifyUrl(src, w)} ${w}w`).join(", ")
    );
    document.head.appendChild(link);

    // Make LCP image eager
    firstImg.loading = "eager";
    firstImg.fetchPriority = "high";
  }

  // ─── MUTATION OBSERVER (Dynamic Content) ─────────────────

  function watchDynamicImages() {
    if (!("MutationObserver" in window)) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType !== 1) return;

          // Direct img tag
          if (node.tagName === "IMG") optimizeImage(node);

          // Images inside added nodes
          node.querySelectorAll?.("img").forEach(optimizeImage);
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // ─── INIT ────────────────────────────────────────────────

  function init() {
    // Optimize all existing images
    document.querySelectorAll("img").forEach(optimizeImage);

    // Setup lazy loading
    setupLazyLoad();

    // Convert GIFs
    convertGifsToVideo();

    // Optimize background images
    optimizeBackgroundImages();

    // Preload LCP
    preloadLCPImage();

    // Watch for new images (infinite scroll, AJAX)
    watchDynamicImages();

    console.log("[ImageOptimizer] ✅ All images optimized");
  }

  // Run on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

})();